import React, { useState, useMemo } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { FaPlus, FaTrash, FaSmile, FaExclamationCircle, FaFileCsv, FaHistory, FaCheck, FaTimes } from 'react-icons/fa';
import StatementGenerator from './StatementGenerator';
import InvoiceGenerator from './InvoiceGenerator';

const AccountStatement = ({ clientId, clientName }) => {
    const { invoices, payments, addPayment, deletePayment, creditNotes, addCreditNote, updateCreditNote, deleteCreditNote } = useData();
    const { isAdmin } = useAuth();
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [showCreditForm, setShowCreditForm] = useState(false);
    const [periodFilter, setPeriodFilter] = useState('all');

    // Form States
    const [newPayment, setNewPayment] = useState({ amount: '', date: new Date().toISOString().split('T')[0], reference: '', type: 'Virement' });
    const [newCredit, setNewCredit] = useState({ amount: '', date: new Date().toISOString().split('T')[0], reference: '', type: 'AV' });

    const normClientName = (clientName || '').trim().toLowerCase();
    const matchesClientName = (val) => normClientName && (val || '').trim().toLowerCase() === normClientName;

    // Filter data for this client
    const clientInvoices = useMemo(() =>
        invoices.filter(inv =>
            (clientId && (inv.clientId === clientId || inv.client_id === clientId)) ||
            matchesClientName(inv.practitionerName) ||
            matchesClientName(inv.practitioner_name) ||
            matchesClientName(inv.clientName)
        ),
        [invoices, clientId, normClientName]);

    const clientPayments = useMemo(() =>
        payments.filter(p =>
            (clientId && (p.clientId === clientId || p.client_id === clientId)) ||
            matchesClientName(p.clientName)
        ),
        [payments, clientId, normClientName]);

    const clientCreditNotes = useMemo(() =>
        creditNotes.filter(n =>
            (clientId && (n.clientId === clientId || n.client_id === clientId)) ||
            matchesClientName(n.clientName)
        ),
        [creditNotes, clientId, normClientName]);

    // Create chronological history with running balance
    const chronologicalHistory = useMemo(() => {
        const allOperations = [
            ...clientInvoices.map(inv => ({
                id: inv.id,
                date: inv.date || inv.createdAt,
                type: 'facture',
                label: `Facture ${inv.invoiceNumber || inv.invoice_number || inv.caseNumber || `#${(inv.id || '').toString().substr(0, 8).toUpperCase()}`}`,
                description: inv.patientName || inv.patient_name || 'Patient',
                debit: parseFloat(inv.amount || inv.total_amount || inv.total || 0),
                credit: 0,
                reference: inv.invoiceNumber || inv.id,
                originalItem: inv
            })),
            ...clientPayments.map(p => {
                const amt = parseFloat(p.amount || 0);
                const isRefund = (p.payment_type || p.type || '').toLowerCase().includes('remboursement');
                return {
                    id: p.id,
                    date: p.date,
                    type: isRefund ? 'remboursement' : 'reglement',
                    label: isRefund ? `Remboursement` : `Règlement ${p.payment_type || p.type || 'RG'}`,
                    description: p.reference || '-',
                    debit: isRefund ? amt : 0,
                    credit: isRefund ? 0 : amt,
                    reference: p.reference,
                    originalItem: p
                };
            }),
            ...clientCreditNotes.map(n => ({
                id: n.id,
                date: n.date,
                type: 'avoir',
                label: n.status === 'pending' ? `Demande d'Avoir (En attente)` : `Avoir`,
                description: n.reference || '-',
                debit: 0,
                credit: n.status === 'pending' ? 0 : parseFloat(n.amount || 0), // Pending credit doesn't affect running balance
                reference: n.reference,
                status: n.status || 'approved',
                amount: parseFloat(n.amount || 0),
                originalItem: n
            }))
        ];

        // Sort by date
        allOperations.sort((a, b) => new Date(a.date) - new Date(b.date));

        // Calculate running balance (only counting approved operations)
        let runningBalance = 0;
        return allOperations.map(op => {
            runningBalance += op.debit - op.credit;
            return { ...op, balance: runningBalance };
        });
    }, [clientInvoices, clientPayments, clientCreditNotes]);

    // Period filter calculations
    const getCutoffDate = (filter) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (filter === '30days') {
            const cutoff = new Date(today);
            cutoff.setDate(today.getDate() - 30);
            return cutoff;
        }
        if (filter === 'thisMonth') {
            return new Date(today.getFullYear(), today.getMonth(), 1);
        }
        if (filter === 'thisYear') {
            return new Date(today.getFullYear(), 0, 1);
        }
        return null;
    };

    const { visibleOperations, previousBalance, hasPrevious } = useMemo(() => {
        if (periodFilter === 'all') {
            return { visibleOperations: chronologicalHistory, previousBalance: 0, hasPrevious: false };
        }

        const cutoff = getCutoffDate(periodFilter);
        if (!cutoff) {
            return { visibleOperations: chronologicalHistory, previousBalance: 0, hasPrevious: false };
        }

        const priorOps = [];
        const currentOps = [];

        chronologicalHistory.forEach(op => {
            const opDate = new Date(op.date);
            if (opDate < cutoff) {
                priorOps.push(op);
            } else {
                currentOps.push(op);
            }
        });

        const hasPrev = priorOps.length > 0;
        const prevBal = hasPrev ? priorOps[priorOps.length - 1].balance : 0;

        return {
            visibleOperations: currentOps,
            previousBalance: prevBal,
            hasPrevious: hasPrev
        };
    }, [chronologicalHistory, periodFilter]);

    // Totals
    const totalDebts = clientInvoices.reduce((sum, inv) => sum + parseFloat(inv.amount || inv.total_amount || inv.total || 0), 0);
    const totalPayments = clientPayments.reduce((sum, p) => {
        const amt = parseFloat(p.amount || 0);
        const isRefund = (p.payment_type || p.type || '').toLowerCase().includes('remboursement');
        return sum + (isRefund ? -amt : amt);
    }, 0);
    const totalCredits = clientCreditNotes
        .filter(n => n.status !== 'pending')
        .reduce((sum, n) => sum + parseFloat(n.amount || 0), 0);
    const balance = totalDebts - totalPayments - totalCredits;

    // Export to CSV
    const exportToCSV = () => {
        const headers = ['Date', 'Type', 'Libellé', 'Référence', 'Débit (€)', 'Crédit (€)', 'Solde (€)'];
        const rows = [];
        
        if (hasPrevious) {
            rows.push([
                '-',
                'Report',
                'Solde antérieur',
                '-',
                '',
                '',
                previousBalance.toFixed(2)
            ]);
        }
        
        visibleOperations.forEach(op => {
            rows.push([
                new Date(op.date).toLocaleDateString('fr-FR'),
                op.status === 'pending' ? 'Demande Avoir' : op.type.charAt(0).toUpperCase() + op.type.slice(1),
                op.label,
                op.description,
                op.debit > 0 ? op.debit.toFixed(2) : '',
                op.credit > 0 ? op.credit.toFixed(2) : '',
                op.balance.toFixed(2)
            ]);
        });

        // Ajouter la ligne de totaux généraux à la fin du CSV
        rows.push([
            'TOTAUX',
            '',
            '',
            '',
            totalDebts.toFixed(2),
            (totalPayments + totalCredits).toFixed(2),
            balance.toFixed(2)
        ]);

        const csvContent = [headers, ...rows].map(row => row.join(';')).join('\n');
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const safeClientName = clientName || 'client';
        link.download = `releve_compte_${safeClientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    // Actions
    const handleAddPayment = (e) => {
        e.preventDefault();
        addPayment({ ...newPayment, clientId, clientName, amount: parseFloat(newPayment.amount) });
        setNewPayment({ amount: '', date: new Date().toISOString().split('T')[0], reference: '', type: 'Virement' });
        setShowPaymentForm(false);
    };

    const handleAddCredit = (e) => {
        e.preventDefault();
        const creditData = { 
            ...newCredit, 
            clientId, 
            clientName, 
            amount: parseFloat(newCredit.amount),
            status: isAdmin ? 'approved' : 'pending' // Admin creates approved directly, client requests pending
        };
        addCreditNote(creditData);
        setNewCredit({ amount: '', date: new Date().toISOString().split('T')[0], reference: '', type: 'AV' });
        setShowCreditForm(false);
        if (!isAdmin) {
            alert("Votre demande d'avoir a été transmise à l'administration pour validation.");
        }
    };

    return (
        <div className="account-statement animate__animated animate__fadeIn">
            {/* Header / Summary */}
            <div className={`alert ${balance > 0.01 ? 'alert-warning' : 'alert-success'} d-flex align-items-center justify-content-between mb-4 shadow-sm border-0`} style={{ borderRadius: '16px', padding: '20px' }}>
                <div className="d-flex align-items-center gap-3">
                    <div className={`icon-circle ${balance > 0.01 ? 'bg-warning-light text-warning' : 'bg-success-light text-success'}`} style={{ width: '56px', height: '56px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: balance > 0.01 ? '#fff7ed' : '#f0fdf4' }}>
                        {balance > 0.01 ? <FaExclamationCircle size={28} /> : <FaSmile size={28} />}
                    </div>
                    <div>
                        <h4 className="mb-0 fw-bold">
                            {balance < -0.01 ? `+ ${Math.abs(balance).toFixed(2)} €` : `${balance.toFixed(2)} €`}
                        </h4>
                        <span className="text-muted small">
                            {balance < -0.01 ? 'Crédit en votre faveur' : `Solde du compte (${balance > 0.01 ? 'Dette client' : 'À jour'})`}
                        </span>
                    </div>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                    <button className="btn btn-outline-secondary" onClick={exportToCSV} title="Exporter Excel">
                        <FaFileCsv className="me-1" /> Excel
                    </button>
                    <StatementGenerator clientId={clientId} clientName={clientName} />
                    {isAdmin && (
                        <button className="btn btn-success fw-bold px-3" onClick={() => setShowPaymentForm(!showPaymentForm)}>
                            <FaPlus className="me-2" /> Règlement
                        </button>
                    )}
                    <button className="btn btn-primary fw-bold px-3" onClick={() => setShowCreditForm(!showCreditForm)}>
                        <FaPlus className="me-2" /> {isAdmin ? 'Avoir' : 'Demander un Avoir'}
                    </button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="row g-3 mb-4">
                <div className="col-md-4">
                    <div className="card border-0 shadow-sm text-center p-3" style={{ borderRadius: '12px' }}>
                        <span className="text-danger fw-bold fs-4">{totalDebts.toFixed(2)} €</span>
                        <span className="text-muted small">Total Facturé</span>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card border-0 shadow-sm text-center p-3" style={{ borderRadius: '12px' }}>
                        <span className="text-success fw-bold fs-4">{(totalPayments + totalCredits).toFixed(2)} €</span>
                        <span className="text-muted small">Total Réglé + Avoirs</span>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card border-0 shadow-sm text-center p-3" style={{ borderRadius: '12px', background: balance > 0.01 ? '#fff7ed' : '#f0fdf4' }}>
                        <span className={`fw-bold fs-4 ${balance > 0.01 ? 'text-warning' : 'text-success'}`}>
                            {balance < -0.01 ? `+ ${Math.abs(balance).toFixed(2)} €` : `${balance.toFixed(2)} €`}
                        </span>
                        <span className="text-muted small">
                            {balance < -0.01 ? 'Avoir disponible' : 'Reste à Payer'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Forms */}
            {showPaymentForm && isAdmin && (
                <div className="card mb-4 bg-light border-success shadow-sm" style={{ borderRadius: '12px' }}>
                    <div className="card-body">
                        <h6 className="fw-bold text-success mb-3">Enregistrer un nouveau règlement</h6>
                        <form onSubmit={handleAddPayment} className="row g-3 align-items-end">
                            <div className="col-md-2">
                                <label className="form-label small fw-bold">Date</label>
                                <input type="date" className="form-control" required value={newPayment.date} onChange={e => setNewPayment({ ...newPayment, date: e.target.value })} />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label small fw-bold">Mode</label>
                                <select className="form-control" value={newPayment.type} onChange={e => setNewPayment({ ...newPayment, type: e.target.value })}>
                                    <option value="Virement">Virement</option>
                                    <option value="Chèque">Chèque</option>
                                    <option value="Espèces">Espèces</option>
                                    <option value="CB">CB</option>
                                    <option value="Remboursement">Remboursement</option>
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label small fw-bold">Référence</label>
                                <input type="text" className="form-control" placeholder="ex: Chèque 1234, Virement..." required value={newPayment.reference} onChange={e => setNewPayment({ ...newPayment, reference: e.target.value })} />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label small fw-bold">Montant (€)</label>
                                <input type="number" step="0.01" className="form-control" placeholder="0.00" required value={newPayment.amount} onChange={e => setNewPayment({ ...newPayment, amount: e.target.value })} />
                            </div>
                            <div className="col-md-2">
                                <button type="submit" className="btn btn-success w-100 fw-bold">Valider</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showCreditForm && (
                <div className="card mb-4 bg-light border-primary shadow-sm" style={{ borderRadius: '12px' }}>
                    <div className="card-body">
                        <h6 className="fw-bold text-primary mb-3">{isAdmin ? 'Créer un nouvel avoir' : 'Demander un nouvel avoir'}</h6>
                        <form onSubmit={handleAddCredit} className="row g-3 align-items-end">
                            <div className="col-md-3">
                                <label className="form-label small fw-bold">Date de demande</label>
                                <input type="date" className="form-control" required value={newCredit.date} onChange={e => setNewCredit({ ...newCredit, date: e.target.value })} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label small fw-bold">Référence / Motif de l'avoir</label>
                                <input type="text" className="form-control" placeholder={isAdmin ? "ex: Erreur facture #123..." : "ex: Remise commerciale, retouche..."} required value={newCredit.reference} onChange={e => setNewCredit({ ...newCredit, reference: e.target.value })} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small fw-bold">Montant souhaité (€)</label>
                                <input type="number" step="0.01" className="form-control" placeholder="0.00" required value={newCredit.amount} onChange={e => setNewCredit({ ...newCredit, amount: e.target.value })} />
                            </div>
                            <div className="col-md-2">
                                <button type="submit" className="btn btn-primary w-100 fw-bold">{isAdmin ? 'Créer' : 'Envoyer la demande'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Chronological History */}
            <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: '16px' }}>
                <div className="card-header bg-white border-bottom-0 py-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <h6 className="mb-0 fw-bold d-flex align-items-center">
                        <FaHistory className="text-primary me-2" /> Historique Chronologique
                    </h6>
                    <div className="d-flex align-items-center gap-2">
                        <select 
                            className="form-select form-select-sm border-secondary-subtle fw-medium shadow-sm" 
                            style={{ width: '185px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' }}
                            value={periodFilter}
                            onChange={(e) => setPeriodFilter(e.target.value)}
                        >
                            <option value="all">Tout afficher</option>
                            <option value="30days">30 derniers jours</option>
                            <option value="thisMonth">Ce mois-ci</option>
                            <option value="thisYear">Cette année</option>
                        </select>
                        <span className="badge bg-secondary">
                            {visibleOperations.length} {visibleOperations.length > 1 ? 'opérations' : 'opération'}
                        </span>
                    </div>
                </div>
                <div className="table-responsive animate__animated animate__fadeIn" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <table className="table table-hover table-sm mb-0 align-middle">
                        <thead className="table-light sticky-top">
                            <tr>
                                <th className="ps-3">Date</th>
                                <th>Type</th>
                                <th>Libellé</th>
                                <th>Référence</th>
                                <th className="text-end text-danger">Débit (€)</th>
                                <th className="text-end text-success">Crédit (€)</th>
                                <th className="text-end">Solde (€)</th>
                                {isAdmin && <th className="text-center">Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {visibleOperations.length === 0 && !hasPrevious ? (
                                <tr><td colSpan={isAdmin ? 8 : 7} className="text-center text-muted py-5">Aucune opération enregistrée sur cette période</td></tr>
                            ) : (
                                <>
                                    {hasPrevious && (
                                        <tr className="table-light" style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                            <td className="ps-3 small text-muted">-</td>
                                            <td>
                                                <span className="badge bg-secondary">Report</span>
                                            </td>
                                            <td className="fw-bold text-muted" colSpan="2">Solde antérieur (Opérations précédentes)</td>
                                            <td className="text-end text-muted">-</td>
                                            <td className="text-end text-muted">-</td>
                                            <td className={`text-end fw-bold ${previousBalance > 0.01 ? 'text-warning' : 'text-success'}`}>
                                                {previousBalance.toFixed(2)}
                                            </td>
                                            {isAdmin && <td></td>}
                                        </tr>
                                    )}
                                    {visibleOperations.map((op, index) => (
                                        <tr key={op.id || index} style={op.status === 'pending' ? { backgroundColor: '#fdfaf2' } : {}}>
                                            <td className="ps-3 small">{new Date(op.date).toLocaleDateString('fr-FR')}</td>
                                            <td>
                                                <span className={`badge ${op.status === 'pending' ? 'bg-warning text-dark' : op.type === 'facture' ? 'bg-danger' : op.type === 'reglement' ? 'bg-success' : op.type === 'remboursement' ? 'bg-info' : 'bg-primary'}`}>
                                                    {op.status === 'pending' ? 'En attente' : op.type === 'facture' ? 'Facture' : op.type === 'reglement' ? 'Règlement' : op.type === 'remboursement' ? 'Remboursement' : 'Avoir'}
                                                </span>
                                            </td>
                                            <td className="fw-bold">
                                                {op.label}
                                                {op.type === 'facture' && op.originalItem && (
                                                    <InvoiceGenerator
                                                        invoice={op.originalItem}
                                                        buttonLabel="Facture"
                                                        showButton={true}
                                                        isExisting={true}
                                                        className="btn btn-xs btn-outline-primary ms-2 py-0 px-2 small fw-bold"
                                                    />
                                                )}
                                            </td>
                                            <td className="small text-muted" style={{ maxWidth: '150px' }} title={op.description}>
                                                {op.description.length > 20 ? op.description.substring(0, 20) + '...' : op.description}
                                            </td>
                                            <td className="text-end text-danger">{op.debit > 0 ? op.debit.toFixed(2) : '-'}</td>
                                            <td className="text-end text-success">
                                                {op.credit > 0 ? op.credit.toFixed(2) : op.status === 'pending' ? <span className="text-muted italic">({op.amount.toFixed(2)} €)</span> : '-'}
                                            </td>
                                            <td className={`text-end fw-bold ${op.balance > 0.01 ? 'text-warning' : 'text-success'}`}>
                                                {op.balance.toFixed(2)}
                                            </td>
                                            {isAdmin && (
                                                <td className="text-center">
                                                    {op.status === 'pending' ? (
                                                        <div className="d-flex justify-content-center gap-2">
                                                            <button
                                                                className="btn btn-link text-success p-0"
                                                                title="Accepter la demande"
                                                                onClick={() => {
                                                                    if (window.confirm('Accepter cette demande d\'avoir ?')) {
                                                                        updateCreditNote(op.id, { status: 'approved' });
                                                                    }
                                                                }}
                                                            >
                                                                <FaCheck size={14} />
                                                            </button>
                                                            <button
                                                                className="btn btn-link text-danger p-0"
                                                                title="Refuser la demande"
                                                                onClick={() => {
                                                                    if (window.confirm('Refuser et supprimer cette demande d\'avoir ?')) {
                                                                        deleteCreditNote(op.id);
                                                                    }
                                                                }}
                                                            >
                                                                <FaTimes size={14} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        op.type !== 'facture' && (
                                                            <button
                                                                className="btn btn-link text-danger p-0"
                                                                title="Supprimer"
                                                                onClick={() => {
                                                                    if (window.confirm('Supprimer cette écriture ?')) {
                                                                        if (op.type === 'reglement') {
                                                                            deletePayment(op.id);
                                                                        } else {
                                                                            deleteCreditNote(op.id);
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                <FaTrash size={12} />
                                                            </button>
                                                        )
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </>
                            )}
                        </tbody>
                        {(visibleOperations.length > 0 || hasPrevious) && (
                            <tfoot className="table-light">
                                <tr className="fw-bold">
                                    <td colSpan="4" className="ps-3">TOTAUX</td>
                                    <td className="text-end text-danger">{totalDebts.toFixed(2)}</td>
                                    <td className="text-end text-success">{(totalPayments + totalCredits).toFixed(2)}</td>
                                    <td className={`text-end ${balance > 0.01 ? 'text-warning' : 'text-success'}`}>{balance.toFixed(2)}</td>
                                    {isAdmin && <td></td>}
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AccountStatement;
